import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useParams } from 'react-router-dom';
import { Flex, Icon, Button, Avatar, Box } from '@chakra-ui/react';
import { ChatSolid, Loader, ChatPrefill } from '@/assets/icons/interview';
import {
  usePostConversationMutation,
  useGetConversationMutation,
} from '@/store/query/interview';
import { sanitizeDom } from '@/lib/purify';
import { useAuth } from '@/hooks';
import { KText } from '@/ui-library/components/text';
import { type InterviewType } from '@/types/interview';
import { XClose } from '@kula-ai/icons/outline/XClose';

// Types
interface ChatMessage {
  id?: number;
  user: 'AI' | 'me';
  message: string;
}

interface ConversationResponse {
  data?: {
    status: string;
    answer: string;
  };
}

interface MessageData {
  msg_id?: string;
  session_id?: string;
}

interface InterviewAssistantProps {
  interview: InterviewType;
  onClose: () => void;
  showFooter?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  interviewId?: string | undefined | number;
}

export interface InterviewAssistantHandle {
  postMessage: (text: string) => void;
  focusInput: () => void;
}

// Constants
const MAX_RETRIES = 20;
const RETRY_INTERVAL = 1000;
const STATIC_MESSAGES: ChatMessage[] = [
  {
    user: 'AI',
    message: 'Hi',
  },
  {
    user: 'AI',
    message:
      'I can help you find information and answer questions about this conversation',
  },
  {
    user: 'AI',
    message: 'Where shall we get started?',
  },
];

const TEMPLATE_MESSAGES = [
  'Summarize key points from this interview in 5 bullets.',
  'Generate a summary of the main discussion points from this interview.',
  'Briefly summarize the key skills and experience the candidate highlighted.',
  'Generate a summary of the main discussion points from this interview.',
];

export const InterviewAssistant = React.memo(
  forwardRef<InterviewAssistantHandle, InterviewAssistantProps>(
    ({ onClose, onLoadingChange, interviewId: interviewIdProp }, ref) => {
      // State
      const [searchInput, setSearchInput] = useState<string>('');
      const [chatData, setChatData] = useState<ChatMessage[]>([]);
      const [isConversationLoading, setIsConversationLoading] =
        useState<boolean>(false);
      const [conversationData, setConversationData] = useState<string | null>(
        null
      );
      const [conversationId, setConversationId] = useState<number | undefined>(
        undefined
      );
      const [conversationError, setConversationError] = useState<Error | null>(
        null
      );

      // Refs
      const chatContainerRef = useRef<HTMLDivElement>(null);

      // Hooks
      const { interviewId: interviewIdParam } = useParams<{
        interviewId: string;
      }>();
      const interviewId = interviewIdProp ?? interviewIdParam;
      const [postConversation, { data: messageData }] =
        usePostConversationMutation();
      const [getConversation] = useGetConversationMutation();
      const { currUser } = useAuth();
      const { msg_id, session_id } = (messageData as MessageData) || {};

      // Utility functions
      const retry = useCallback(
        async <T,>(
          fn: () => Promise<T>,
          retries: number,
          delay: number
        ): Promise<T> => {
          try {
            return await fn();
          } catch (error) {
            if (retries > 1) {
              await new Promise((resolve) => setTimeout(resolve, delay));
              return retry(fn, retries - 1, delay);
            }
            throw error;
          }
        },
        []
      );

      const getMessage = useCallback(async (): Promise<string> => {
        if (!msg_id) return '';

        // eslint-disable-next-line no-useless-catch -- pre-existing; behavior preserved (extraction move)
        try {
          const res = (await getConversation({
            interviewId,
            msg_id,
          })) as ConversationResponse;
          if (res?.data?.status !== 'success') {
            throw new Error(res?.data?.answer || 'Failed to fetch message');
          }
          return res?.data?.answer;
        } catch (error) {
          throw error;
        }
      }, [msg_id, interviewId, getConversation]);

      const fetchConversation = useCallback(async (): Promise<void> => {
        setIsConversationLoading(true);
        setConversationError(null);
        try {
          const data = await retry(getMessage, MAX_RETRIES, RETRY_INTERVAL);
          setConversationData(data);
          setConversationId(Date.now());
        } catch (error) {
          console.error('Error fetching conversation:', error);
          setConversationError(error as Error);
        } finally {
          setIsConversationLoading(false);
        }
      }, [getMessage, retry]);

      const updateChat = useCallback((chat: ChatMessage): void => {
        if (!chat.message) return;

        const chatWithId = { ...chat, id: Date.now() };
        setChatData((prevChatData) => [...prevChatData, chatWithId]);
      }, []);

      const formatHTML = useCallback(
        (verbose: string | null | undefined): string =>
          (verbose ?? '')
            .split('\n')
            .map((sentence) => (sentence ? `<p>${sentence}</p>` : '<br />'))
            .join(''),
        []
      );

      const handlePostChat = useCallback(
        (text?: string): void => {
          const message = searchInput.trim() || text;
          if (!message) return;

          postConversation({
            interviewId,
            message,
            session_id,
          });
          updateChat({ user: 'me', message });
          setSearchInput('');
        },
        [searchInput, interviewId, session_id, postConversation, updateChat]
      );

      // Expose imperative methods to parent (for external input CTA)
      const inputRef = useRef<HTMLTextAreaElement | null>(null);
      useImperativeHandle(
        ref,
        () => ({
          postMessage: (text: string) => handlePostChat(text),
          focusInput: () => inputRef.current?.focus(),
        }),
        [handlePostChat]
      );

      // Effects
      useEffect(() => {
        if (msg_id) {
          fetchConversation();
        }
      }, [msg_id, fetchConversation]);

      useEffect(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }, [chatData, conversationError, isConversationLoading]);

      useEffect(() => {
        if (conversationData) {
          updateChat({ user: 'AI', message: formatHTML(conversationData) });
        }
      }, [conversationData, conversationId, updateChat, formatHTML]);

      useEffect(() => {
        if (conversationError !== null) {
          updateChat({
            user: 'AI',
            message: 'Unable to generate the text. Please try again.',
          });
        }
      }, [conversationError, updateChat]);

      // Notify parent about loading state changes
      useEffect(() => {
        onLoadingChange?.(isConversationLoading);
      }, [isConversationLoading, onLoadingChange]);

      // Render functions
      const renderIcon = useCallback(
        (isAiIcon = true) => {
          const { first_name, last_name, avatar } = currUser;
          return isAiIcon ? (
            <Flex
              w="32px"
              h="32px"
              bg="#605AFB"
              borderRadius="6px"
              justifyContent="center"
              alignItems="center"
            >
              <Icon as={ChatSolid} w="14px" h="14px" color="#FFF" />
            </Flex>
          ) : (
            <Avatar
              src={avatar}
              name={`${first_name} ${last_name}`}
              size="sm"
              borderRadius="6px"
              alignSelf="flex-end"
            />
          );
        },
        [currUser]
      );

      const renderTemplate = useCallback(
        (text: string) => (
          <Flex
            p="17px 10px"
            border="1px solid #DCCCFF"
            w="calc(50% - 12px)"
            borderRadius="sm"
            cursor="pointer"
            _hover={{ borderColor: '#454CE7' }}
            onClick={() => {
              setSearchInput(text);
              handlePostChat(text);
            }}
          >
            <Icon as={ChatPrefill} w="25px" h="25px" mr="8px" />
            <KText size="sm">{text}</KText>
          </Flex>
        ),
        [handlePostChat]
      );

      const renderMessage = useCallback(
        (text: string, user: 'AI' | 'me', index: number, needIcon = true) => (
          <React.Fragment key={`message-${index}`}>
            {needIcon && renderIcon(user === 'AI')}
            <Box
              p="8px"
              w="fit-content"
              maxW="calc(100% - 100px)"
              borderRadius="sm"
              bg={user !== 'AI' ? 'primary-100' : 'secondary-50'}
              alignSelf={user !== 'AI' ? 'flex-end' : 'flex-start'}
              fontSize="14px"
              dangerouslySetInnerHTML={{ __html: sanitizeDom(text) }}
            />
          </React.Fragment>
        ),
        [renderIcon]
      );

      const renderSpinner = useCallback(
        () => <Icon as={Loader} w="40px" h="" />,
        []
      );

      const renderChatWrapper = useMemo(
        () => (
          <Flex
            flex="1"
            flexDirection="column"
            justifyContent="flex-end"
            overflow="hidden"
            pb="12px"
          >
            <Flex
              flexDirection="column"
              overflowY="auto"
              pr="24px"
              pl="24px"
              mr="0px"
              ref={chatContainerRef}
            >
              <Flex gap="10px" pb="12px" flexDirection="column">
                {renderIcon()}
                {STATIC_MESSAGES.map(({ message, user }, index) =>
                  renderMessage(message, user, index, false)
                )}
              </Flex>
              {chatData.length === 0 ? (
                <Flex gap="12px" flexDirection="row" pb="12px" flexWrap="wrap">
                  {TEMPLATE_MESSAGES.map((text, index) => (
                    <React.Fragment key={`template-${index}`}>
                      {renderTemplate(text)}
                    </React.Fragment>
                  ))}
                </Flex>
              ) : (
                <Flex gap="10px" pb="12px" flexDirection="column">
                  {chatData.map(({ message, user }, index) =>
                    renderMessage(message, user, index)
                  )}
                  {isConversationLoading && renderSpinner()}
                </Flex>
              )}
            </Flex>
          </Flex>
        ),
        [
          chatData,
          isConversationLoading,
          renderIcon,
          renderMessage,
          renderSpinner,
          renderTemplate,
        ]
      );

      return (
        <Flex
          flexDirection="column"
          flex={1}
          height="100%"
          overflow="hidden"
          my="12px"
          position="relative"
        >
          <Button
            position="absolute"
            top="12px"
            right="12px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            bg="base-white"
            _active={{}}
            _focus={{}}
            onClick={() => onClose()}
            variant="quaternary"
            zIndex="1"
            w="28px"
            h="28px"
            minW="28px"
            p="0"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={XClose} w="16px" h="16px" color="secondary-700" />
          </Button>
          {renderChatWrapper}
        </Flex>
      );
    }
  )
);
